import {
  containsTemplateRef,
  formatTemplateToken,
  formatToolCallParamBindingRef,
  parseToolCallParamBindingRef,
  splitTemplate,
  WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID,
} from '@cotera/utilities';
import { WorkflowNodeType } from './types';
import { workflowNodeFromGraphNode } from './resolve-workflow-node-output-sample';

/** Node-data key holding the `sourceId/pointer` map, by node type. */
const BINDINGS_FIELD_BY_NODE_TYPE: ReadonlyMap<string, string> = new Map([
  [WorkflowNodeType.ToolCall, 'paramBindings'],
  [WorkflowNodeType.EmitEvent, 'payloadBindings'],
  [WorkflowNodeType.RunWorkflow, 'inputBindings'],
  [WorkflowNodeType.Mapper, 'fieldBindings'],
]);

/** Minimal graph node shape the heal needs (definition graph or draft graph). */
export type BindingSourceHealNode = {
  readonly id: string;
  readonly type: string;
  readonly data?: unknown;
};

export type BindingSourceRepair = {
  readonly nodeId: string;
  /** Where in the node's data the ref lived, e.g. `payloadBindings.channel`. */
  readonly location: string;
  readonly from: string;
  readonly to: string;
};

export type HealBindingSourcesResult<T> = {
  readonly nodes: readonly T[];
  readonly repairs: readonly BindingSourceRepair[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Display names a ref could plausibly have used for this node: the SDK's own
 * `WorkflowNode.name` plus anything the caller can resolve that the graph alone
 * can't (an agent's title, which lives on the agent, not the node).
 */
function aliasesForNode(
  node: BindingSourceHealNode,
  extraAliases: ReadonlyMap<string, readonly string[]> | undefined
): readonly string[] {
  const sdkName = workflowNodeFromGraphNode({
    id: node.id,
    type: node.type,
    data: node.data,
  }).name;
  return [sdkName, ...(extraAliases?.get(node.id) ?? [])];
}

/**
 * Maps every unambiguous display name to its node id. A name claimed by more
 * than one node maps to `null` — an ambiguous ref is left alone rather than
 * guessed at.
 */
function buildNodeIdByAlias(
  nodes: readonly BindingSourceHealNode[],
  extraAliases: ReadonlyMap<string, readonly string[]> | undefined
): ReadonlyMap<string, string | null> {
  const byAlias = new Map<string, string | null>();
  for (const node of nodes) {
    for (const alias of aliasesForNode(node, extraAliases)) {
      const trimmed = alias.trim();
      if (trimmed === '' || trimmed === node.id) {
        continue;
      }
      byAlias.set(trimmed, byAlias.has(trimmed) ? null : node.id);
    }
  }
  return byAlias;
}

/**
 * Rewrites one `sourceName/pointer` ref when its source segment is a display
 * name rather than a node id. Returns `null` when there is nothing to change —
 * the source already resolves, is the trigger sentinel, or matches no node.
 */
function healRef(
  ref: string,
  nodeIds: ReadonlySet<string>,
  nodeIdByAlias: ReadonlyMap<string, string | null>
): string | null {
  let parsed: ReturnType<typeof parseToolCallParamBindingRef>;
  try {
    parsed = parseToolCallParamBindingRef(ref);
  } catch {
    // Not a node-scoped ref (bare pointer, or free text in a `{{}}`): leave it.
    return null;
  }
  const source = parsed.sourceName;
  if (
    source === undefined ||
    source === WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID ||
    nodeIds.has(source)
  ) {
    return null;
  }
  const resolved = nodeIdByAlias.get(source);
  if (resolved === undefined || resolved === null) {
    return null;
  }
  return formatToolCallParamBindingRef(resolved, parsed.pointer);
}

/** Rewrites every `{{sourceName/pointer}}` token embedded in a string param. */
function healTemplateString(
  text: string,
  nodeIds: ReadonlySet<string>,
  nodeIdByAlias: ReadonlyMap<string, string | null>
): string | null {
  const segments = splitTemplate(text);
  if (!segments.some((segment) => segment.t === 'ref')) {
    return null;
  }
  let changed = false;
  const healed = segments
    .map((segment) => {
      if (segment.t === 'text') {
        return segment.value;
      }
      const next = healRef(segment.ref, nodeIds, nodeIdByAlias);
      if (next === null) {
        return formatTemplateToken(segment.ref);
      }
      changed = true;
      return formatTemplateToken(next);
    })
    .join('');
  return changed ? healed : null;
}

/** Walks nested params/values, healing template tokens in every string leaf. */
function healTemplateValue(
  value: unknown,
  path: string,
  nodeIds: ReadonlySet<string>,
  nodeIdByAlias: ReadonlyMap<string, string | null>,
  onRepair: (location: string, from: string, to: string) => void
): unknown {
  if (typeof value === 'string') {
    const healed = healTemplateString(value, nodeIds, nodeIdByAlias);
    if (healed === null) {
      return value;
    }
    onRepair(path, value, healed);
    return healed;
  }
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry, index) => {
      const healed = healTemplateValue(
        entry,
        `${path}[${index}]`,
        nodeIds,
        nodeIdByAlias,
        onRepair
      );
      changed = changed || healed !== entry;
      return healed;
    });
    return changed ? next : value;
  }
  if (isRecord(value)) {
    let changed = false;
    const next: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      const healed = healTemplateValue(
        entry,
        `${path}.${key}`,
        nodeIds,
        nodeIdByAlias,
        onRepair
      );
      changed = changed || healed !== entry;
      next[key] = healed;
    }
    return changed ? next : value;
  }
  return value;
}

function healNodeData(
  node: BindingSourceHealNode,
  nodeIds: ReadonlySet<string>,
  nodeIdByAlias: ReadonlyMap<string, string | null>,
  repairs: BindingSourceRepair[]
): unknown | null {
  if (!isRecord(node.data)) {
    return null;
  }
  const bindingsField = BINDINGS_FIELD_BY_NODE_TYPE.get(node.type);
  const next: Record<string, unknown> = {};
  let changed = false;

  for (const [key, value] of Object.entries(node.data)) {
    if (key === bindingsField && isRecord(value)) {
      const bindings: Record<string, unknown> = {};
      for (const [paramKey, ref] of Object.entries(value)) {
        // A mapper's field can hold a `{{ref}}` template rather than a bare
        // ref, and a template reads as a nonsense source segment to `healRef`.
        const healed =
          typeof ref !== 'string'
            ? null
            : containsTemplateRef(ref)
            ? healTemplateString(ref, nodeIds, nodeIdByAlias)
            : healRef(ref, nodeIds, nodeIdByAlias);
        if (healed === null || typeof ref !== 'string') {
          bindings[paramKey] = ref;
          continue;
        }
        changed = true;
        repairs.push({
          nodeId: node.id,
          location: `${key}.${paramKey}`,
          from: ref,
          to: healed,
        });
        bindings[paramKey] = healed;
      }
      next[key] = bindings;
      continue;
    }

    const healed = healTemplateValue(
      value,
      key,
      nodeIds,
      nodeIdByAlias,
      (location, from, to) => {
        changed = true;
        repairs.push({ nodeId: node.id, location, from, to });
      }
    );
    next[key] = healed;
  }

  return changed ? next : null;
}

/**
 * Repairs binding refs whose source segment is a node's DISPLAY NAME instead of
 * its id — `{ "channel": "Agent 3946c56f…/channel" }` becomes
 * `{ "channel": "<agent node uuid>/channel" }`.
 *
 * Coco authored graphs this way before `BINDING_REF_LLM_INSTRUCTIONS` told it
 * to use ids. Those refs resolve to nothing at run time, and since the save
 * path checks source membership structurally
 * (`validateToolCallParamBindings`), they also make the whole workflow
 * unsaveable — a graph the editor can open but never persist again.
 *
 * Both the `paramBindings`/`payloadBindings`/`inputBindings` maps and
 * `{{ref}}` tokens embedded in string params are healed. A name that no node
 * claims, or that two nodes share, is left untouched: the point is to recover
 * refs whose intent is unambiguous, not to guess.
 *
 * Pure and idempotent — a healed graph heals to itself, and nodes with nothing
 * to fix are returned by identity so callers can cheaply detect a no-op.
 */
export function healWorkflowBindingSources<
  T extends BindingSourceHealNode
>(params: {
  readonly nodes: readonly T[];
  /**
   * Display names the graph can't derive on its own, keyed by node id — an
   * agent node's title lives on the agent, so the client passes it in once the
   * agent list has loaded.
   */
  readonly extraAliasesByNodeId?: ReadonlyMap<string, readonly string[]>;
}): HealBindingSourcesResult<T> {
  const nodeIds = new Set(params.nodes.map((node) => node.id));
  const nodeIdByAlias = buildNodeIdByAlias(
    params.nodes,
    params.extraAliasesByNodeId
  );
  const repairs: BindingSourceRepair[] = [];
  const nodes = params.nodes.map((node) => {
    const data = healNodeData(node, nodeIds, nodeIdByAlias, repairs);
    return data === null ? node : { ...node, data };
  });
  return { nodes, repairs };
}
